import { cn } from "#core/cn";
import { Icon } from "#icon/icon";
import { InputGroup, InputGroupAddon, InputGroupInput } from "#components/input-group";
import { Kbd } from "#components/kbd";

import type { SearchEntry, SiteSearch } from "./search.types";

type SearchBarProps<TEntry extends SearchEntry> = {
	search: SiteSearch<TEntry>;
	placeholder: string;
	noResults: string;
	// The key hint in the trailing addon, such as "⌘K".
	shortcutHint: string;
};

// Search bar for an app shell: a controlled input with a results dropdown that
// navigates to the matched page. State and ranking live in useSiteSearch.
export function SearchBar<TEntry extends SearchEntry>({
	search,
	placeholder,
	noResults,
	shortcutHint,
}: SearchBarProps<TEntry>) {
	const {
		query,
		open,
		setOpen,
		results,
		activeIndex,
		setActiveIndex,
		onQueryChange,
		onKeyDown,
		select,
		inputRef,
	} = search;

	const showDropdown = open && query.trim() !== "";

	return (
		<div className="relative w-full">
			<InputGroup>
				<InputGroupAddon>
					<Icon name="search" />
				</InputGroupAddon>
				<InputGroupInput
					ref={inputRef}
					placeholder={placeholder}
					value={query}
					onChange={(e) => onQueryChange(e.target.value)}
					onFocus={() => setOpen(true)}
					onBlur={() => setOpen(false)}
					onKeyDown={onKeyDown}
				/>
				<InputGroupAddon align="inline-end">
					<Kbd>{shortcutHint}</Kbd>
				</InputGroupAddon>
			</InputGroup>

			{showDropdown ? (
				<div className="bg-popover text-popover-foreground absolute top-full left-0 z-30 mt-1 w-full overflow-hidden rounded-md border p-1 shadow-md">
					{results.length === 0 ? (
						<div className="text-muted-foreground px-2 py-1.5 text-sm">{noResults}</div>
					) : (
						results.map((result, i) => {
							const EntryIcon = result.entry.icon;
							return (
								<button
									key={result.entry.to}
									type="button"
									// preventDefault on mousedown keeps focus in the input so
									// the blur-close does not fire before this click navigates.
									onMouseDown={(e) => e.preventDefault()}
									onClick={() => select(result)}
									onMouseEnter={() => setActiveIndex(i)}
									className={cn(
										"flex w-full items-center gap-3 rounded-sm px-2 py-1.5 text-left text-sm",
										i === activeIndex && "bg-accent",
									)}
								>
									<EntryIcon className="text-muted-foreground size-4 shrink-0" />
									<span className="flex min-w-0 flex-col">
										<span className="truncate">{result.entry.label}</span>
										<span className="text-muted-foreground truncate text-xs">
											{result.entry.description}
										</span>
									</span>
								</button>
							);
						})
					)}
				</div>
			) : null}
		</div>
	);
}
